import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import SplashScreen from './pages/SplashScreen';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import RFQChoice from './pages/RFQChoice';
import CustomerRegistration from './pages/CustomerRegistration';
import RFQRegistration from './pages/RFQRegistration';
import Customers from './pages/Customers';
import Reports from './pages/Reports';
import Analysis from './pages/Analysis';
import ImportExport from './pages/ImportExport';
import PotentialCustomers from './pages/PotentialCustomers';
import QuotationCosting from './pages/QuotationCosting';
import QuotationCostingList from './pages/QuotationCostingList';
import TenantSetup from './pages/TenantSetup';
import MasterControl from './pages/MasterControl';
import Settings from './pages/Settings';
import { Toaster } from './components/ui/sonner';
import { FeaturesProvider, useFeatures } from './context/FeaturesContext';

// Check if user has valid session
function hasSession() {
  return document.cookie.includes("rfq_session=") || 
         !!localStorage.getItem("rfq_token") || 
         !!localStorage.getItem("auth_token");
}

// Check if user is master
function isMaster() {
  try {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      const user = JSON.parse(userData);
      return user.role === 'master' || user.username === 'MASTERCONTROL24';
    }
  } catch {}
  return false;
}

// Protected route
function PrivateRoute({ children }) {
  return hasSession() ? children : <Navigate to="/login" replace />;
}

// Master-only route
function MasterRoute({ children }) {
  if (!hasSession()) return <Navigate to="/login" replace />;
  if (!isMaster()) return <Navigate to="/dashboard" replace />;
  return children;
}

// Feature-gated route wrapper
const FeatureRoute = ({ children, feature }) => {
  const { isFeatureEnabled, loading } = useFeatures();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2540]"></div>
      </div>
    );
  }
  
  if (!isFeatureEnabled(feature)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Module Not Available</h1>
          <p className="text-gray-600 mb-4">
            The "{feature}" module is not enabled for your account.
          </p>
          <button 
            onClick={() => window.history.back()} 
            className="mt-4 px-4 py-2 bg-[#0A2540] text-white rounded hover:bg-[#0A2540]/90"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  return children;
};

function AppContent() {
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  if (showSplash) {
    return <SplashScreen />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/tenant-setup" element={<TenantSetup />} />
      
      {/* Master Control Panel */}
      <Route path="/master" element={<MasterRoute><MasterControl /></MasterRoute>} />
      
      {/* Dashboard */}
      <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      
      {/* Feature-gated Routes */}
      <Route path="/rfq" element={<PrivateRoute><FeatureRoute feature="rfq"><RFQChoice /></FeatureRoute></PrivateRoute>} />
      <Route path="/customer-registration" element={<PrivateRoute><FeatureRoute feature="rfq"><CustomerRegistration /></FeatureRoute></PrivateRoute>} />
      <Route path="/rfq-registration" element={<PrivateRoute><FeatureRoute feature="rfq"><RFQRegistration /></FeatureRoute></PrivateRoute>} />
      <Route path="/customers" element={<PrivateRoute><FeatureRoute feature="customers"><Customers /></FeatureRoute></PrivateRoute>} />
      <Route path="/reports" element={<PrivateRoute><FeatureRoute feature="reports"><Reports /></FeatureRoute></PrivateRoute>} />
      <Route path="/analysis" element={<PrivateRoute><FeatureRoute feature="analysis"><Analysis /></FeatureRoute></PrivateRoute>} />
      <Route path="/import-export" element={<PrivateRoute><FeatureRoute feature="import_export"><ImportExport /></FeatureRoute></PrivateRoute>} />
      <Route path="/potential-customers" element={<PrivateRoute><FeatureRoute feature="onboarding"><PotentialCustomers /></FeatureRoute></PrivateRoute>} />
      <Route path="/quotation-costing" element={<PrivateRoute><FeatureRoute feature="quotation"><QuotationCosting /></FeatureRoute></PrivateRoute>} />
      <Route path="/quotation-costing/:id" element={<PrivateRoute><FeatureRoute feature="quotation"><QuotationCosting /></FeatureRoute></PrivateRoute>} />
      <Route path="/quotation-costing-list" element={<PrivateRoute><FeatureRoute feature="quotation"><QuotationCostingList /></FeatureRoute></PrivateRoute>} />
      
      {/* Settings - available to all users */}
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <FeaturesProvider>
          <AppContent />
        </FeaturesProvider>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
